import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiPlus, 
  FiEdit3, 
  FiTrash2, 
  FiUsers, 
  FiEye, 
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiMail,
  FiPhone,
  FiUser,
  FiAward,
  FiLink,
  FiX,
  FiCheck
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { colaboradorService, cargoService, congregacaoService } from '../../services/api';
import api from '../../services/api';
import './Colaboradores.css';

const Colaboradores = () => {
  const navigate = useNavigate();
  const [colaboradores, setColaboradores] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroTipoPessoa, setFiltroTipoPessoa] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroCargo, setFiltroCargo] = useState('todos');
  const [colaboradorParaExcluir, setColaboradorParaExcluir] = useState(null);
  const [mostrarModalExclusao, setMostrarModalExclusao] = useState(false);

  // ── Modal "Vincular da Irmandade" ─────────────────────────────────────────
  const [modalVinculo, setModalVinculo] = useState(false);
  const [membrosIrmandade, setMembrosIrmandade] = useState([]);
  const [cargosVinculo, setCargosVinculo] = useState([]);
  const [congregacoesVinculo, setCongregatoesVinculo] = useState([]);
  const [loadingVinculo, setLoadingVinculo] = useState(false);
  const [salvandoVinculo, setSalvandoVinculo] = useState(false);
  const [buscaMembro, setBuscaMembro] = useState('');
  const [membroSelecionado, setMembroSelecionado] = useState(null);
  const [cargosVinculoSelecionados, setCargosVinculoSelecionados] = useState([]);
  const [congregacaoVinculo, setCongregacaoVinculo] = useState('');
  const buscaRef = useRef(null);

  useEffect(() => {
    carregarDados();
  }, []);

  // ── Lógica do modal de vínculo ────────────────────────────────────────────
  const abrirModalVinculo = async () => {
    setModalVinculo(true);
    setMembroSelecionado(null);
    setBuscaMembro('');
    setCargosVinculoSelecionados([]);
    setCongregacaoVinculo('');
    setLoadingVinculo(true);
    try {
      const [resJovens, resAdultos, resCargos, resCongregacoes] = await Promise.all([
        api.get('/irmandade/todos', { params: { categoria: 'jovem' } }),
        api.get('/irmandade/todos', { params: { categoria: 'adulto' } }),
        cargoService.getAll(),
        congregacaoService.getAtivas(),
      ]);
      const jovens = Array.isArray(resJovens.data) ? resJovens.data : [];
      const adultos = Array.isArray(resAdultos.data) ? resAdultos.data : [];
      // IDs já vinculados a algum colaborador
      const idsJaVinculados = new Set(
        colaboradores
          .filter(c => c.irmandade_id || c.irmandadeId)
          .map(c => c.irmandade_id ?? c.irmandadeId)
      );
      const todos = [...jovens, ...adultos]
        .filter(m => !idsJaVinculados.has(m.id))
        .sort((a, b) => (a.nome_completo || '').localeCompare(b.nome_completo || '', 'pt-BR'));
      setMembrosIrmandade(todos);
      setCargosVinculo(resCargos.data || []);
      if (resCongregacoes.success) setCongregatoesVinculo(resCongregacoes.data || []);
    } catch {
      toast.error('Erro ao carregar dados para vínculo.');
    } finally {
      setLoadingVinculo(false);
      setTimeout(() => buscaRef.current?.focus(), 100);
    }
  };

  const selecionarMembro = (m) => {
    setMembroSelecionado(m);
    setBuscaMembro(m.nome_completo || '');
    // Pré-selecionar congregação do membro
    if (m.congregacao) {
      const cong = congregacoesVinculo.find(c => c.nome === m.congregacao);
      if (cong) setCongregacaoVinculo(String(cong.id));
    }
  };

  const toggleCargoVinculo = (cargoId) => {
    setCargosVinculoSelecionados(prev =>
      prev.includes(cargoId) ? prev.filter(id => id !== cargoId) : [...prev, cargoId]
    );
  };

  const salvarVinculo = async () => {
    if (!membroSelecionado) { toast.warn('Selecione um membro.'); return; }
    if (cargosVinculoSelecionados.length === 0) { toast.warn('Selecione pelo menos um cargo.'); return; }
    if (!congregacaoVinculo) { toast.warn('Selecione a congregação.'); return; }
    setSalvandoVinculo(true);
    try {
      await api.post('/colaboradores', {
        nome_completo: membroSelecionado.nome_completo,
        tipo_pessoa: membroSelecionado.tipo_pessoa,
        cargo_ids: cargosVinculoSelecionados,
        telefone: membroSelecionado.telefone || null,
        email: membroSelecionado.email || null,
        congregacao_id: congregacaoVinculo ? parseInt(congregacaoVinculo, 10) : null,
        irmandade_id: membroSelecionado.id,
      });
      toast.success(`${membroSelecionado.nome_completo} vinculado com sucesso!`);
      setModalVinculo(false);
      carregarDados();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Erro ao vincular membro.';
      toast.error(msg);
    } finally {
      setSalvandoVinculo(false);
    }
  };

  const membrosFiltrados = membrosIrmandade.filter(m =>
    !buscaMembro.trim() ||
    (m.nome_completo || '').toLowerCase().includes(buscaMembro.toLowerCase())
  );

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [colaboradoresResponse, cargosResponse] = await Promise.all([
        colaboradorService.getAll({ com_membros_cargos: true }),
        cargoService.getAll()
      ]);
      
      if (colaboradoresResponse.success) {
        setColaboradores(colaboradoresResponse.data || []);
      }
      
      if (cargosResponse.success) {
        setCargos(cargosResponse.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const confirmarExclusao = (colaborador) => {
    setColaboradorParaExcluir(colaborador);
    setMostrarModalExclusao(true);
  };

  const excluirColaborador = async () => {
    if (!colaboradorParaExcluir || colaboradorParaExcluir.origem === 'membro_cargo') return;

    try {
      const response = await colaboradorService.delete(colaboradorParaExcluir.id);
      if (response.success) {
        toast.success('Colaborador excluído com sucesso!');
        carregarDados();
      } else {
        toast.error(response.message || 'Erro ao excluir colaborador');
      }
    } catch (error) {
      console.error('Erro ao excluir colaborador:', error);
      toast.error('Erro ao excluir colaborador');
    } finally {
      setMostrarModalExclusao(false);
      setColaboradorParaExcluir(null);
    }
  };

  const toggleStatus = async (colaborador) => {
    if (colaborador.origem === 'membro_cargo') {
      toast.info('Membro cadastrado na Irmandade: altere o status em Irmandade.');
      return;
    }
    try {
      const dadosAtualizados = {
        ...colaborador,
        ativo: !colaborador.ativo
      };

      const response = await colaboradorService.update(colaborador.id, dadosAtualizados);
      if (response.success) {
        toast.success(`Colaborador ${colaborador.ativo ? 'desativado' : 'ativado'} com sucesso!`);
        carregarDados();
      } else {
        toast.error(response.message || 'Erro ao alterar status do colaborador');
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status do colaborador');
    }
  };

  const nomeExibicao = (c) => (c.nome_completo || c.nomeCompleto || '').trim();

  const colaboradoresFiltrados = colaboradores.filter(colaborador => {
    // Verificações de segurança para evitar erro de undefined
    const nomeCompleto = nomeExibicao(colaborador);
    const email = colaborador.email || '';
    const telefone = colaborador.telefone || '';
    const congregacao = colaborador.congregacao || '';
    const cargoNome = colaborador.cargo_nome || '';
    const irmandadeNome = colaborador.irmandade_nome || colaborador.irmandadeNome || '';
    
    const matchSearch = nomeCompleto.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       telefone.includes(searchTerm) ||
                       congregacao.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       cargoNome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       irmandadeNome.toLowerCase().includes(searchTerm.toLowerCase());
    
    const tipoP = colaborador.tipo_pessoa || colaborador.tipoPessoa;
    const matchTipoPessoa = filtroTipoPessoa === 'todos' || tipoP === filtroTipoPessoa;
    const matchStatus = filtroStatus === 'todos' || 
                       (filtroStatus === 'ativo' && colaborador.ativo) ||
                       (filtroStatus === 'inativo' && !colaborador.ativo);
    
    // Verificar se o colaborador tem o cargo selecionado (suporte a múltiplos cargos)
    const matchCargo = filtroCargo === 'todos' || 
                      (colaborador.cargo_ids && colaborador.cargo_ids.includes(parseInt(filtroCargo))) ||
                      (colaborador.cargo_id && colaborador.cargo_id.toString() === filtroCargo);
    
    return matchSearch && matchTipoPessoa && matchStatus && matchCargo;
  });

  const obterNomeCargo = (cargoId) => {
    const cargo = cargos.find(c => c.id === cargoId);
    return cargo ? cargo.nome : 'Sem cargo';
  };

  const obterCargosColaborador = (colaborador) => {
    // Se tem cargo_nome (múltiplos cargos concatenados), usar isso
    if (colaborador.cargo_nome) {
      return colaborador.cargo_nome.split(', ');
    }
    
    // Se tem cargo_ids array, mapear para nomes
    if (colaborador.cargo_ids && Array.isArray(colaborador.cargo_ids)) {
      return colaborador.cargo_ids.map(id => obterNomeCargo(id));
    }
    
    // Fallback para cargo_id único
    if (colaborador.cargo_id) {
      return [obterNomeCargo(colaborador.cargo_id)];
    }
    
    return ['Sem cargo'];
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <FiRefreshCw className="spin" />
          <p>Carregando colaboradores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="colaboradores-page">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <FiUsers className="page-icon" />
            Gerenciar Colaboradores
          </h1>
          <p>Gerencie os colaboradores da congregação</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            className="btn-vincular-irmandade"
            onClick={abrirModalVinculo}
          >
            <FiLink /> Vincular da Irmandade
          </button>
          <button
            className="btn-primary"
            onClick={() => navigate('/colaboradores/cadastro')}
          >
            <FiPlus /> Novo Colaborador
          </button>
        </div>
      </div>

      <div className="page-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por nome, email, telefone, congregação ou membro vinculado..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <FiFilter className="filter-icon" />
            <select 
              value={filtroTipoPessoa} 
              onChange={(e) => setFiltroTipoPessoa(e.target.value)}
            >
              <option value="todos">Todos</option>
              <option value="irmao">Irmãos</option>
              <option value="irma">Irmãs</option>
            </select>
          </div>

          <div className="filter-group">
            <select 
              value={filtroStatus} 
              onChange={(e) => setFiltroStatus(e.target.value)}
            >
              <option value="todos">Todos Status</option>
              <option value="ativo">Ativos</option>
              <option value="inativo">Inativos</option>
            </select>
          </div>

          <div className="filter-group">
            <select 
              value={filtroCargo} 
              onChange={(e) => setFiltroCargo(e.target.value)}
            >
              <option value="todos">Todos Cargos</option>
              {cargos.map(cargo => (
                <option key={cargo.id} value={cargo.id.toString()}>
                  {cargo.nome}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          className="btn-refresh"
          onClick={carregarDados}
          title="Atualizar lista"
        >
          <FiRefreshCw />
        </button>
      </div>

      <div className="colaboradores-stats">
        <div className="stat-card">
          <span className="stat-number">{colaboradores.length}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-card active">
          <span className="stat-number">{colaboradores.filter(c => c.ativo).length}</span>
          <span className="stat-label">Ativos</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{colaboradores.filter(c => (c.tipo_pessoa || c.tipoPessoa) === 'irmao').length}</span>
          <span className="stat-label">Irmãos</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{colaboradores.filter(c => (c.tipo_pessoa || c.tipoPessoa) === 'irma').length}</span>
          <span className="stat-label">Irmãs</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{colaboradoresFiltrados.length}</span>
          <span className="stat-label">Filtrados</span>
        </div>
      </div>

      {colaboradoresFiltrados.length === 0 ? (
        <div className="empty-state">
          <FiUsers className="empty-icon" />
          <h3>Nenhum colaborador encontrado</h3>
          <p>
            {searchTerm || filtroTipoPessoa !== 'todos' || filtroStatus !== 'todos' || filtroCargo !== 'todos'
              ? 'Tente ajustar os filtros de busca'
              : 'Comece cadastrando o primeiro colaborador'
            }
          </p>
          {!searchTerm && filtroTipoPessoa === 'todos' && filtroStatus === 'todos' && filtroCargo === 'todos' && (
            <button
              className="btn-primary"
              onClick={() => navigate('/colaboradores/cadastro')}
            >
              <FiPlus /> Cadastrar Primeiro Colaborador
            </button>
          )}
        </div>
      ) : (
        <div className="colaboradores-grid">
          {colaboradoresFiltrados.map(colaborador => (
            <div
              key={colaborador.id != null ? colaborador.id : `membro-cargo-${colaborador.irmandade_id}`}
              className={`colaborador-card ${!colaborador.ativo ? 'inactive' : ''}`}
            >
              <div className="colaborador-header">
                <div className="colaborador-avatar">
                  <FiUser className="avatar-icon" />
                  <span className="tipo-pessoa">{(colaborador.tipo_pessoa || colaborador.tipoPessoa) === 'irmao' ? '♂️' : '♀️'}</span>
                </div>
                <div className="colaborador-basic">
                  <h3>{nomeExibicao(colaborador) || 'Sem nome'}</h3>
                  <div className="cargos-container">
                    {obterCargosColaborador(colaborador).map((cargoNome, index) => (
                      cargoNome !== 'Sem cargo' && (
                        <span key={index} className="cargo-badge">
                          <FiAward />
                          {cargoNome}
                        </span>
                      )
                    ))}
                    {obterCargosColaborador(colaborador).every(cargo => cargo === 'Sem cargo') && (
                      <span className="cargo-badge sem-cargo">
                        <FiAward />
                        Sem cargo
                      </span>
                    )}
                  </div>
                  {(colaborador.irmandade_nome || colaborador.irmandadeNome) && (
                    <p className="colaborador-vinculo">
                      Vinculado a: <strong>{colaborador.irmandade_nome || colaborador.irmandadeNome}</strong>
                      {(colaborador.irmandade_categoria || colaborador.irmandadeCategoria) && (
                        <span className="vinculo-categoria"> ({colaborador.irmandade_categoria === 'jovem' || colaborador.irmandadeCategoria === 'jovem' ? 'Jovem' : 'Adulto'})</span>
                      )}
                    </p>
                  )}
                </div>
                <div className="colaborador-actions">
                  <button
                    className="btn-icon"
                    onClick={() =>
                      colaborador.origem === 'membro_cargo' && colaborador.irmandade_id
                        ? navigate(`/irmandade/editar/${colaborador.irmandade_id}`)
                        : navigate(`/colaboradores/editar/${colaborador.id}`)
                    }
                    title={colaborador.origem === 'membro_cargo' ? 'Abrir cadastro na Irmandade' : 'Visualizar / editar'}
                  >
                    <FiEye />
                  </button>
                  <button
                    className="btn-icon edit"
                    onClick={() =>
                      colaborador.origem === 'membro_cargo' && colaborador.irmandade_id
                        ? navigate(`/irmandade/editar/${colaborador.irmandade_id}`)
                        : navigate(`/colaboradores/editar/${colaborador.id}`)
                    }
                    title="Editar"
                  >
                    <FiEdit3 />
                  </button>
                  {colaborador.origem !== 'membro_cargo' && (
                    <button
                      className="btn-icon delete"
                      onClick={() => confirmarExclusao(colaborador)}
                      title="Excluir"
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="colaborador-body">
                <div className="contact-info">
                  {colaborador.email && (
                    <div className="contact-item">
                      <FiMail />
                      <span>{colaborador.email}</span>
                    </div>
                  )}
                  {colaborador.telefone && (
                    <div className="contact-item">
                      <FiPhone />
                      <span>{colaborador.telefone}</span>
                    </div>
                  )}
                </div>
                
                <div className="colaborador-info">
                  {colaborador.congregacao && (
                    <span className="congregacao">
                      {colaborador.congregacao}
                    </span>
                  )}
                  
                  <span className={`status-badge ${colaborador.ativo ? 'active' : 'inactive'}`}>
                    {colaborador.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>

              <div className="colaborador-footer">
                {colaborador.origem !== 'membro_cargo' ? (
                  <button
                    className={`btn-toggle ${colaborador.ativo ? 'deactivate' : 'activate'}`}
                    onClick={() => toggleStatus(colaborador)}
                  >
                    {colaborador.ativo ? 'Desativar' : 'Ativar'}
                  </button>
                ) : (
                  <span className="hint-membro-cargo" title="Cadastro na Irmandade">
                    Cargo vinculado na Irmandade
                  </span>
                )}
                {colaborador.created_at && !isNaN(new Date(colaborador.created_at).getTime()) && (
                  <small className="created-date">
                    Desde: {new Date(colaborador.created_at).toLocaleDateString('pt-BR')}
                  </small>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Modal Vincular da Irmandade ── */}
      {modalVinculo && (
        <div className="modal-overlay" onClick={() => setModalVinculo(false)}>
          <div className="modal-vinculo-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FiLink /> Vincular membro da Irmandade</h3>
              <button className="modal-vinculo-close" onClick={() => setModalVinculo(false)} aria-label="Fechar">
                <FiX size={20} />
              </button>
            </div>

            {loadingVinculo ? (
              <div className="modal-vinculo-loading">
                <FiRefreshCw className="spin" /> Carregando membros...
              </div>
            ) : (
              <div className="modal-vinculo-body">
                {/* Busca de membro */}
                <div className="vinculo-secao">
                  <label className="vinculo-label">1. Buscar membro</label>
                  <div className="vinculo-busca-wrap">
                    <FiSearch className="vinculo-busca-icone" />
                    <input
                      ref={buscaRef}
                      type="search"
                      className="vinculo-busca-input"
                      placeholder="Digite o nome..."
                      value={buscaMembro}
                      onChange={e => {
                        setBuscaMembro(e.target.value);
                        if (membroSelecionado && e.target.value !== membroSelecionado.nome_completo) {
                          setMembroSelecionado(null);
                        }
                      }}
                    />
                  </div>

                  {/* Lista de resultados */}
                  {!membroSelecionado && buscaMembro.trim().length > 0 && (
                    <ul className="vinculo-lista">
                      {membrosFiltrados.length === 0 ? (
                        <li className="vinculo-lista-vazio">Nenhum membro encontrado.</li>
                      ) : (
                        membrosFiltrados.slice(0, 10).map(m => (
                          <li key={m.id}
                            className="vinculo-lista-item"
                            onClick={() => selecionarMembro(m)}
                          >
                            <span className="vinculo-item-nome">{m.nome_completo}</span>
                            <span className="vinculo-item-cat">
                              {m.categoria === 'jovem' ? 'Jovem' : 'Adulto'}
                            </span>
                          </li>
                        ))
                      )}
                    </ul>
                  )}

                  {/* Card do membro selecionado */}
                  {membroSelecionado && (
                    <div className="vinculo-membro-card">
                      <div className="vinculo-membro-avatar">
                        {(membroSelecionado.nome_completo || '').charAt(0).toUpperCase()}
                      </div>
                      <div className="vinculo-membro-info">
                        <strong>{membroSelecionado.nome_completo}</strong>
                        <span>{membroSelecionado.categoria === 'jovem' ? 'Jovem' : 'Adulto'}</span>
                        {membroSelecionado.telefone && <span>{membroSelecionado.telefone}</span>}
                        {membroSelecionado.email && <span>{membroSelecionado.email}</span>}
                      </div>
                      <button className="vinculo-membro-trocar"
                        onClick={() => { setMembroSelecionado(null); setBuscaMembro(''); }}
                        title="Trocar membro">
                        <FiX size={14} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Cargos */}
                <div className="vinculo-secao">
                  <label className="vinculo-label">2. Cargo(s)</label>
                  <div className="vinculo-cargos-grid">
                    {cargosVinculo.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        className={`vinculo-cargo-btn${cargosVinculoSelecionados.includes(c.id) ? ' ativo' : ''}`}
                        onClick={() => toggleCargoVinculo(c.id)}
                      >
                        {cargosVinculoSelecionados.includes(c.id) && <FiCheck size={12} />}
                        {c.nome}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Congregação */}
                <div className="vinculo-secao">
                  <label className="vinculo-label">3. Congregação</label>
                  <select
                    className="vinculo-select"
                    value={congregacaoVinculo}
                    onChange={e => setCongregacaoVinculo(e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    {congregacoesVinculo.map(c => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="modal-vinculo-footer">
              <button className="btn-secondary" onClick={() => setModalVinculo(false)}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={salvarVinculo}
                disabled={salvandoVinculo || !membroSelecionado || cargosVinculoSelecionados.length === 0 || !congregacaoVinculo}
              >
                {salvandoVinculo ? <><FiRefreshCw className="spin" /> Salvando...</> : <><FiLink /> Vincular</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {mostrarModalExclusao && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirmar Exclusão</h3>
            </div>
            <div className="modal-body">
              <p>
                Tem certeza que deseja excluir o colaborador <strong>{colaboradorParaExcluir ? nomeExibicao(colaboradorParaExcluir) : ''}</strong>?
              </p>
              <p className="warning">
                ⚠️ Esta ação não pode ser desfeita. Todos os registros de presença 
                e histórico deste colaborador serão perdidos.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setMostrarModalExclusao(false);
                  setColaboradorParaExcluir(null);
                }}
              >
                Cancelar
              </button>
              <button
                className="btn-danger"
                onClick={excluirColaborador}
              >
                <FiTrash2 /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Colaboradores;